import { TextInput, UploadArea } from "../..";
import {
  ActivityPicker,
  DistrictPicker,
  GooglePlacesSelect,
  PremiseCategoryPicker,
  PremiseTypePicker,
  SubActivityPicker, RegionPicker,
} from "containers";
import { useFormik } from "formik";
import { FC, useEffect } from "react";
import { classNames, validators, wrapClick } from "utils";
import { ICreateServiceRequestFormSchema, PropertyInfoSchema } from "./schema";
import { gql, useLazyQuery } from "@apollo/client";
// import { MapView } from "@ui/cards/map-view";

export interface PropertyInfoFormProps {
  handleNext: (values: ICreateServiceRequestFormSchema["propertyInfo"]) => void;
  handlePrevious: () => void;
  initialValues: ICreateServiceRequestFormSchema["propertyInfo"];
  values: ICreateServiceRequestFormSchema;
  handleCancel: () => void;
}

const RESOLVE_ADDRESS_INFO = gql`
  query ResolveGhanaPostAddress($address: String!) {
    address: resolveGhanaPostAddress(address: $address) {
      _id
      postCode
      district
      region
      area
      address
      street
      coordinates {
        longitude
        latitude
      }
    }
  }
`;

export const PropertyInfoForm: FC<PropertyInfoFormProps> = ({
  initialValues,
  handleNext,
  handlePrevious,
  handleCancel,
}) => {
  const form = useFormik<ICreateServiceRequestFormSchema["propertyInfo"]>({
    initialValues,
    validationSchema: PropertyInfoSchema,
    onSubmit: (values) => {
      handleNext(values);
    },
    onReset: () => {
      handleCancel?.();
    },
  });

  const [resolveGhanaPostAddress] = useLazyQuery(RESOLVE_ADDRESS_INFO);

  useEffect(() => {
    if (form.values.ghanaPostAddress.match(validators.GhanaPostRegex)) {
      resolveGhanaPostAddress({
        variables: {
          address: form.values.ghanaPostAddress,
        },
      })
        .then(({ data, error }) => {
          if (data?.address?._id) {
            if (data.address?.coordinates) {
              form.setFieldValue("geoLocation", {
                longitude: data.address.coordinates.longitude,
                latitude: data.address.coordinates.latitude,
              });
            }
            if (data.address?.street) {
              form.setFieldValue("streetName", data.address.street);
            }
            if (data.address?.area) {
              form.setFieldValue("community", data.address.area);
            }
          } else if (error) {
            form.setFieldTouched("ghanaPostAddress", true);
            form.setFieldError("ghanaPostAddress", error?.message);
          }
        })
        .catch((err) => {
          form.setFieldTouched("ghanaPostAddress", true);
          form.setFieldError("ghanaPostAddress", "Invalid Ghana Post Address");
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.ghanaPostAddress]);

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="space-y-6 divide-y divide-gray-200 p-6 flex-1 overflow-y-auto">
        <div className="">
          <span className="text-xs font-light">Owner Information</span>
          <div className="grid grid-cols-3 gap-6 mt-2">
            <div className="col-span-2">
              <TextInput
                id="owner.fullName"
                label="Full Name"
                type="text"
                placeholder="e.g. Atta Adwoa"
                required={true}
                {...form}
              />
            </div>
            <div>
              <TextInput
                id="owner.phoneNumber"
                label="Phone Number"
                type="text"
                placeholder="e.g. 0550123292"
                required={true}
                maxLength={10}
                {...form}
              />
            </div>
          </div>
        </div>
        <div className="pt-6">
          <span className="text-xs font-light">Property Information</span>
          <div className="grid grid-cols-3 gap-6 mt-2">
            <div>
              <RegionPicker
                id="region"
                label="Region"
                placeholder="Region Name"
                required={true}
                {...form}
                setFieldValue={(field: string, value: string) => {
                  form.setFieldValue("district", null);
                  form.setFieldValue(field, value);
                }}
              />
            </div>
            {form.values.region?._id && (
              <div>
                <DistrictPicker
                  id="district"
                  label="District"
                  placeholder="District Name"
                  required={true}
                  filter={{ region: form.values.region?._id}}
                  {...form}
                  setFieldValue={(field: string, value: string) => {
                    form.setFieldValue(field, value);
                  }}
                />
              </div>
            )}
            <div className="col-start-1">
              <TextInput
                id="ghanaPostAddress"
                label="Ghana Post Address"
                type="text"
                maxLength={12}
                minLength={11}
                placeholder="eg. GH-2323-2323"
                required={true}
                {...form}
              />
            </div>
            <div className="">
              <TextInput
                id="streetName"
                label="Street Name"
                type="text"
                placeholder="eg. Orange Street"
                required={true}
                {...form}
              />
            </div>
            <div className="">
              <TextInput
                id="houseNumber"
                label="House Number"
                type="text"
                placeholder="eg. 67"
                required={true}
                {...form}
              />
            </div>
            <div className="">
              <TextInput
                id="community"
                label="Community"
                type="text"
                placeholder="eg. Market Circle"
                required={true}
                {...form}
              />
            </div>
            <div className="col-span-2">
              <GooglePlacesSelect
                id="landmark"
                label="Closest Landmark"
                placeholder="eg. Under the house"
                required={true}
                {...form}
              />
            </div>
          </div>
        </div>
        <div className="pt-6">
          <span className="text-xs font-light">Premise Information</span>
          <div className="grid grid-cols-3 gap-6 mt-2">
            <div className="col-start-1">
              <PremiseTypePicker
                id="premiseType"
                label="Premise Type"
                placeholder="e.g. Accomodation"
                required={true}
                {...form}
                setFieldValue={(field: string, value: string) => {
                  form.setFieldValue("premiseCategory", null);
                  form.setFieldValue(field, value);
                }}
              />
            </div>
            <div className="col-span-2">
              <PremiseCategoryPicker
                id="premiseCategory"
                label="Premise Category"
                placeholder="e.g. Hotel"
                required={true}
                disabled={!form.values.premiseType}
                {...form}
                filter={{
                  premiseType: form?.values?.premiseType?._id,
                }}
              />
            </div>
            <div className="col-start-1">
              <ActivityPicker
                id="activity"
                label="Activity"
                placeholder="e.g. Hospitality"
                required={true}
                {...form}
                setFieldValue={(field: string, value: string) => {
                  form.setFieldValue("subActivity", null);
                  form.setFieldValue(field, value);
                }}
              />
            </div>
            <div className="col-span-2">
              <SubActivityPicker
                id="subActivity"
                label="Sub Activity"
                placeholder="e.g. Guest House"
                required={true}
                disabled={!form.values.activity}
                {...form}
                filter={{
                  activity: form?.values?.activity?._id,
                }}
              />
            </div>
          </div>
        </div>
        <div className="pt-6">
          <span className="text-xs font-light">Site Plan Information</span>
          <div className="mt-2">
            <UploadArea
              id="sitePlanDocumentUrl"
              label="Upload Site Plan"
              accept={{
                "image/*": [".png", ".jpeg", ".jpg"],
                "application/pdf": [".pdf"],
              }}
              className="w-full h-96"
              maxSize={5 * 1024 * 1024}
              {...form}
            />
          </div>
        </div>
        <div className="pt-6">
          <span className="text-xs font-light">Map Information</span>
          {form.values?.geoLocation ? (
            <div className="mt-2 w-full h-96 rounded-md overflow-hidden">
              {/*
              <MapView
                longitude={form.values?.geoLocation?.longitude}
                latitude={form.values?.geoLocation?.latitude}
              />
              */}
            </div>
          ) : (
            <div></div>
          )}
        </div>
      </div>

      <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 sm:py-4 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200">
        <button
          type="button"
          onClick={wrapClick(form.handleSubmit)}
          className={classNames(
            true ? "hover:bg-primary-700" : "cursor-not-allowed",
            "w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm",
          )}
        >
          Next
        </button>
        <button
          type="button"
          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-900 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 hover:dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
          onClick={wrapClick(handlePrevious)}
        >
          Previous
        </button>
        <button
          type="button"
          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-900 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 hover:dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
          onClick={wrapClick(form.resetForm)}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default PropertyInfoForm;
